import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { BehaviorSubject } from "rxjs";
import { UserGroups } from "../models/user";
import { UserDetails } from "../models/users";
import { EnvironmentService } from "./environment.service";

@Injectable({
  providedIn: "root",
})
export class GroupsService {
  private detailsUpdated = new BehaviorSubject<boolean>(false);
  public detailsUpdated$ = this.detailsUpdated.asObservable();

  sendDetailsUpdated(val: boolean) {
    this.detailsUpdated.next(val);
  }

  private selectedGroup = new BehaviorSubject<UserGroups>(null);
  public selectedGroup$ = this.selectedGroup.asObservable();

  updateSelectedGroup(group: UserGroups) {
    this.selectedGroup.next(group);
  }

  constructor(private http: HttpClient, private envs: EnvironmentService) {}

  // groups crud
  getAllUserGroups() {
    let baseUrl =
      this.envs.envVariables.httpProtocol +
      this.envs.envVariables.webHost +
      "UserInfoes/GetUserGroups";
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache");
    return this.http.get(baseUrl, { headers: headers });
  }

  addUserGroup(group: UserGroups) {
    let baseUrl =
      this.envs.envVariables.httpProtocol +
      this.envs.envVariables.webHost +
      "UserInfoes/AddUserGroup";
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache");
    return this.http.post(baseUrl, group, { headers: headers });
  }

  editUserGroup(oldGroupName: string, updatedGroup: UserGroups) {
    let baseUrl =
      this.envs.envVariables.httpProtocol +
      this.envs.envVariables.webHost +
      "UserInfoes/UpdateUserGroup/groupName=" +
      oldGroupName;
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache");
    return this.http.put(baseUrl, updatedGroup, { headers: headers });
  }

  deleteUserGroup(groupName: string) {
    let baseUrl =
      this.envs.envVariables.httpProtocol +
      this.envs.envVariables.webHost +
      "UserInfoes/DeleteUserGroup/groupName=" +
      groupName;
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache");
    return this.http.delete(baseUrl, { headers: headers });
  }

  updateUsersInGroup(groupName: string, policyJson: any) {
    let baseUrl =
      this.envs.envVariables.httpProtocol +
      this.envs.envVariables.webHost +
      "UserInfoes/UpdateUserInGroup/groupName=" +
      groupName;
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache");
    return this.http.put(baseUrl, policyJson, { headers: headers });
  }

  uploadStartUpFile(groupName: string, file: File) {
    const formData = new FormData();
    formData.append("File", file);
    let baseUrl =
      this.envs.envVariables.httpProtocol +
      this.envs.envVariables.webHost +
      `UserInfoes/${groupName}/AddStartupFile`;
    let headers = new HttpHeaders();
    headers = headers
      .set("Cache-Control", "no-cache")
      .set("Access-Control-Allow-Origin", "*")
      .set(
        "Access-Control-Allow-Headers",
        "Origin, X-Requested-With, Content-Type, Accept"
      );

    const upload$ = this.http.post<any>(baseUrl, formData, {
      headers: headers,
      reportProgress: true,
      observe: "events",
    });

    return upload$;
  }

  removeStartUpFile(groupName: string, fileName) {
    let baseUrl =
      this.envs.envVariables.httpProtocol +
      this.envs.envVariables.webHost +
      `UserInfoes/${groupName}/RemoveStartupFile`;
    let headers = new HttpHeaders();
    headers = headers
      .set("Cache-Control", "no-cache")
      .set("Access-Control-Allow-Origin", "*")
      .set(
        "Access-Control-Allow-Headers",
        "Origin, X-Requested-With, Content-Type, Accept"
      )
      .set("filename", fileName);

    return this.http.delete<any>(baseUrl, { headers: headers });
  }

  // groups access control crud
  getAccessControlForGroup(groupName: string) {
    let baseUrl =
      this.envs.envVariables.httpProtocol +
      this.envs.envVariables.webHost +
      `UserInfoes/Group/${groupName}/GetGroupProfileInfo`;
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache");
    return this.http.get(baseUrl, { headers: headers });
  }

  detachAccessOfDeploymentFromGroup(groupName: string, depName: string) {
    let baseUrl =
      this.envs.envVariables.httpProtocol +
      this.envs.envVariables.webHost +
      `UserInfoes/Group/${groupName}/DetachDeployment/${depName}`;
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache");
    return this.http.delete(baseUrl, { headers: headers });
  }

  attachAccessOfDeploymentToGroup(groupName: string, depDetails: UserDetails) {
    let baseUrl =
      this.envs.envVariables.httpProtocol +
      this.envs.envVariables.webHost +
      `UserInfoes/Group/${groupName}/AttachDeployment`;
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache");
    return this.http.post(baseUrl, depDetails, { headers: headers });
  }

  updateDeploymentInfoOfGroup(groupName: string, depDetails: UserDetails) {
    let baseUrl =
      this.envs.envVariables.httpProtocol +
      this.envs.envVariables.webHost +
      `UserInfoes/Group/${groupName}/UpdateGroupProfile/${depDetails.deploymentName}`;
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache");
    return this.http.put(baseUrl, depDetails, { headers: headers });
  }

  addUserGroupRdpFile(groupName: string, rdpFile: File, fileType: string) {
    try {
      const formData = new FormData();
      formData.append("File", rdpFile);

      let baseUrl =
        this.envs.envVariables.httpProtocol +
        this.envs.envVariables.webHost +
        "UserInfoes/Group/" +
        groupName +
        "/UploadGroupRdpFile";
      let headers = new HttpHeaders();
      headers = headers
        .set("Cache-Control", "no-cache")
        .set("Access-Control-Allow-Origin", "*")
        .set(
          "Access-Control-Allow-Headers",
          "Origin, X-Requested-With, Content-Type, Accept"
        )
        .set("FileType", fileType);

      const upload$ = this.http.post<any>(baseUrl, formData, {
        headers: headers,
        reportProgress: true,
        observe: "events",
      });

      return upload$;
    } catch (Error) {
      throw Error;
    }
  }
}
